import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, X } from 'lucide-react';
import AccountForm from '../ui/accountForm';
import constant from '../../constant';

const FloatingAlert = ({ message, onClose }) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000);
        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md shadow-md z-50">
            <div className="flex items-center justify-between">
                <span className="block sm:inline">{message}</span>
                <button onClick={onClose} className="ml-4 focus:outline-none">
                    <X className="h-4 w-4 text-green-700 opacity-75 hover:opacity-100" />
                </button>
            </div>
        </div>
    );
};


const Account = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [userRole, setUserRole] = useState('');
    const [formData, setFormData] = useState({
        companyName: '',
        companyPhone: '',
        companyEmail: '',
        gst: '',
        companyLogo: null,
        signature: null,
        streetAddress: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India',
        bankName: '',
        accountName: '',
        accountNumber: '',
        ifsc: '',
        terms: '',
        startFrom: 0,
        prefix: '',
        isGstExempted: false,
        dashboardImage: null,
        imsfreezeDate: null,
        freezePeriodStart: null,
        freezePeriodEnd: null,
        imsfreezeDateList: []
    });

    // Moved outside useEffect for reuse
    const constructApiUrl = (endpoint) => {
        const currentUserRole = localStorage.getItem("role"); // Use fresh value
        const adminSelectedUser = localStorage.getItem("adminSelectedUser");
        let apiUrl = `${constant.REACT_APP_API_URL}${endpoint}`;
        // Define endpoints requiring userId even for admins viewing others
        const requiresUserIdEndpoints = ['/user/profile', '/user/uploads'];
        const requiresUserId = requiresUserIdEndpoints.some(prefix => endpoint.startsWith(prefix));

        if (currentUserRole === "admin" && adminSelectedUser && requiresUserId) {
            apiUrl = `${constant.REACT_APP_API_URL}${endpoint}${endpoint.includes("?") ? "&" : "?"}userId=${adminSelectedUser}`;
        }
        return apiUrl;
    };

    useEffect(() => {
        const role = localStorage.getItem("role") || '';
        setUserRole(role); // Set user role state

        const fetchProfile = async () => {
            setLoading(true);
            setError(null);
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setError('No authentication token found');
                    setLoading(false);
                    return;
                }

                const apiUrl = constructApiUrl("/user/profile");


                const response = await fetch(apiUrl, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                if (!response.ok) {
                    const errorData = await response.json();
                    console.error("Fetch profile error response:", errorData);
                    throw new Error(`Failed to fetch profile: ${response.statusText}`);
                }
                const data = await response.json();


                const company = data.data?.company || {};
                const address = data.data?.address || {};
                const bankDetails = data.data?.bankDetails || {};
                const invoice = data.data?.invoice || {};
                const sales = data.data?.sales || {};

                setFormData({
                    companyName: data.data?.companyName || '',
                    companyPhone: company.mobile || '',
                    companyEmail: company.email || '',
                    gst: data.data?.gst || '',
                    companyLogo: company.logo || null,
                    signature: company.signature || null,
                    streetAddress: address.streetAddress || '',
                    city: address.city || '',
                    state: address.state || '',
                    pincode: address.pincode || '',
                    country: address.country || 'India',
                    bankName: bankDetails.bankName || '',
                    accountName: bankDetails.accountName || '',
                    accountNumber: bankDetails.accountNumber || '',
                    ifsc: bankDetails.ifsc || '',
                    terms: data.data?.terms || '',
                    startFrom: invoice.startFrom || 0,
                    prefix: invoice.prefix || '',
                    isGstExempted: data.data?.isGstExempted || false,
                    dashboardImage: company.dashboard || null,
                    imsfreezeDate: data.data?.imsfreezeDate || null,
                    freezePeriodStart: sales.freezePeriodStart || null,
                    freezePeriodEnd: sales.freezePeriodEnd || null,
                    imsfreezeDateList: data.data?.imsfreezeDateList || []
                });

            } catch (err) {
                console.error("Error fetching profile:", err);
                setError(err.message || 'An unknown error occurred while fetching profile.');
            } finally {
                setLoading(false);
            }
        };

        fetchProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // constructApiUrl doesn't need to be a dependency if defined outside

    const handleFileUpload = async (file, fileType) => {
        const token = localStorage.getItem('token');
        if (!token) {
            throw new Error('No authentication token found');
        }

        // Use the constructApiUrl to potentially add userId for admin uploads
        const apiUrl = constructApiUrl("/user/uploads");


        const apiFormData = new FormData();
        apiFormData.append('file', file);
        apiFormData.append('filetype', fileType);

        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: apiFormData
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error(`Upload failed for ${fileType}:`, errorData);
                throw new Error(errorData.message || `Failed to upload ${fileType}`);
            }

            const result = await response.json();


            let stateKey;
            switch (fileType) {
                case 'logo':
                    stateKey = 'companyLogo';
                    break;
                case 'signature':
                    stateKey = 'signature';
                    break;
                case 'dashboard':
                    stateKey = 'dashboardImage';
                    break;
                default:
                    console.warn("Unhandled file type in handleFileUpload:", fileType);
                    return;
            }

            setFormData(prevState => ({
                ...prevState,
                [stateKey]: result.data.url
            }));
            setSuccessMessage(`${fileType.charAt(0).toUpperCase() + fileType.slice(1)} uploaded successfully!`);
            window.location.reload();

        } catch (err) {
            console.error(`Error uploading ${fileType}:`, err);
            throw err;
        }
    };

    const handleSubmit = async (updatedData) => {
        setSubmitLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('No authentication token found');
                setSubmitLoading(false);
                return;
            }

            const apiUrl = constructApiUrl("/user/profile");


            const payload = {
                companyName: updatedData.companyName,
                company: {
                    mobile: updatedData.companyPhone,
                    email: updatedData.companyEmail,
                    logo: typeof formData.companyLogo === 'string' ? formData.companyLogo : null,
                    signature: typeof formData.signature === 'string' ? formData.signature : null,
                },
                gst: updatedData.isGstExempted ? '' : updatedData.gst,
                address: {
                    streetAddress: updatedData.streetAddress,
                    city: updatedData.city,
                    state: updatedData.state,
                    country: updatedData.country,
                    pincode: updatedData.pincode
                },
                bankDetails: {
                    bankName: updatedData.bankName,
                    accountName: updatedData.accountName,
                    accountNumber: updatedData.accountNumber,
                    ifsc: updatedData.ifsc
                },
                invoice: {
                    startFrom: updatedData.startFrom,
                    prefix: updatedData.prefix
                },
                terms: updatedData.terms,
                isGstExempted: updatedData.isGstExempted,
                imsfreezeDate: updatedData.imsfreezeDate,
                sales: {
                    freezePeriodStart: updatedData.freezePeriodStart,
                    freezePeriodEnd: updatedData.freezePeriodEnd
                },
                imsfreezeDateList: updatedData.imsfreezeDateList
            };

            const response = await fetch(apiUrl, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error("Profile update failed:", errorData);
                throw new Error(errorData.message || 'Failed to update profile');
            }

            const result = await response.json();


            setSuccessMessage('Profile updated successfully!');
            setError(null);

        } catch (err) {
            console.error("Error submitting profile update:", err);
            setError(err.message || 'An unknown error occurred during profile update.');
            setSuccessMessage(null);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleCancel = () => {
        navigate(-1);
    };

    const handleCloseSuccess = () => {
        setSuccessMessage(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
            </div>
        );
    }

    // Initial loading error before data is fetched
    if (error && !formData.companyName && !loading) {
        return <div className="text-center py-10 text-red-500">Error loading profile: {error}</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6 relative pt-20 md:pt-6">
            {/* Success Message */}
            {successMessage && (
                <FloatingAlert message={successMessage} onClose={handleCloseSuccess} />
            )}
            {/* Submission Error Message (only show if data exists, avoiding clash with loading error) */}
            {error && formData.companyName && !submitLoading && (
                <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md shadow-md z-50">
                    <div className="flex items-center justify-between">
                        <span className="block sm:inline">Error: {error}</span>
                        <button onClick={() => setError(null)} className="ml-4 focus:outline-none">
                            <X className="h-4 w-4 text-red-700 opacity-75 hover:opacity-100" />
                        </button>
                    </div>
                </div>
            )}


            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                    <div className="flex items-center space-x-4">
                        <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700 transition-colors">
                            <ArrowLeft className="h-6 w-6" />
                        </button>
                        <h2 className="text-2xl font-bold text-gray-800">Account Settings</h2>
                    </div>
                </div>

                <AccountForm
                    initialFormData={formData}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                    onFileUpload={handleFileUpload}
                    isSubmitting={submitLoading}
                    userRole={userRole}
                />
            </div>
        </div>
    );
};

export default Account;